<!--@format-->
<template>
    <div class="solution-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ widgetTitle }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <loading-screen-svg v-if="isLoading" />
                <div v-else class="form-wrapper">
                    <div class="bordered-container">
                        <div class="title">Details</div>
                        <div class="input-container">
                            <app-input
                                v-model="solution.name"
                                label="Name *"
                                placeholder="Item name"
                                :is-valid="!submitted || $v.solution.name.required"
                                error="Name is required"
                            />
                        </div>
                        <div class="input-container">
                            <app-input
                                v-model="solution.sku"
                                label="SKU"
                                placeholder="SKU number"
                            />
                        </div>
                        <div class="input-container">
                            <label>Solution Type</label>
                            <solution-type-dropdown v-model="solution.solutionType" />
                        </div>
                        <div class="input-container">
                            <div class="solution-category-unit-wrapper">
                                <global-category-dropdown
                                    :account-id="solution.accountId"
                                    :selected="solution.categories"
                                    :show-selected-up="true"
                                    @input="addCategory"
                                    @remove="deleteCategory"
                                />
                            </div>
                        </div>
                        <div class="input-container">
                            <label>Description</label>
                            <app-textarea
                                v-model="solution.description"
                                placeholder="Start typing to add a description of this item..."
                            />
                        </div>
                        <div class="input-container">
                            <label>Product Image</label>
                            <div v-if="solution.productImage" class="preview-image-solutions">
                                <div class="trash-can img" @click="deleteFile">
                                    <trash-can-svg />
                                </div>
                                <img
                                    :src="solution.productImage"
                                    :alt="solution.productImageName"
                                />
                            </div>
                            <app-drag-and-drop-area
                                v-else
                                label="Choose an image source or drag image here "
                                type="image"
                                :file-accept="imageFileConfig.fileSelectorAllowedTypes"
                                icon-library
                                @icon-library-opened="setContext('product')"
                                @library-opened="setContext('product')"
                                @upload="handleUpload"
                                @remove="deleteFile"
                                @library:selected="handleCollateralSelected"
                                @icon:selected="handleIconSelected"
                            >
                                <template #upload-icon>
                                    <f6-upload-cloud-svg />
                                </template>
                                <template #library-icon>
                                    <r21-library-outline-svg />
                                </template>
                            </app-drag-and-drop-area>
                            <div class="requirements">
                                <label>
                                    Supported file types:
                                    {{ imageFileConfig.fileSelectorAllowedTypes.join(', ') }}</label
                                >
                                <label>
                                    Maximum upload file size: <span>{{ imageSizeLimit }}</span>
                                </label>
                            </div>
                        </div>
                        <div class="input-container">
                            <label>Video Description (optional)</label>
                            <div v-if="solution.videoUrl" class="preview-video-solutions">
                                <div class="trash-can video" @click="handleDelete">
                                    <trash-can-svg></trash-can-svg>
                                </div>
                                <div v-if="isValidYoutubeUrl(solution.videoUrl)" class="ratio-box">
                                    <iframe
                                        id="`yt-player`"
                                        :src="youtubeEmbedUrl"
                                        title="YouTube video player"
                                        frameborder="0"
                                        width="256"
                                        height="144"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowfullscreen
                                        referrerpolicy="strict-origin-when-cross-origin"
                                    >
                                    </iframe>
                                </div>
                                <video v-else controls="controls" :poster="solution.videoThumbnail">
                                    <source :src="solution.videoUrl" type="video/mp4" />
                                    "Your browser does not support the video tag"
                                </video>
                            </div>
                            <loading-screen-svg v-else-if="isVideoLoading" />
                            <app-drag-and-drop-area
                                v-else
                                type="video"
                                label="Or provide a YouTube URL"
                                :file-accept="videoFileConfig.fileSelectorAllowedTypes"
                                @upload="handleVideoUpload"
                                @library-opened="setContext('video')"
                                @library:selected="handleLibrarySelect"
                            >
                                <template #upload-icon>
                                    <f6-upload-cloud-svg />
                                </template>
                                <template #library-icon>
                                    <r21-library-outline-svg />
                                </template>
                                <template slot="URLInput">
                                    <app-input
                                        class="video-input"
                                        :value="solution.videoUrl"
                                        placeholder="www.yourvideo.com"
                                        :is-valid="!hasError"
                                        error="Please enter a valid Youtube link"
                                        @input="handleInput"
                                    />
                                </template>
                            </app-drag-and-drop-area>
                            <div class="requirements">
                                <label>
                                    Supported file types:
                                    {{ videoFileConfig.fileSelectorAllowedTypes.join(', ') }}
                                </label>
                                <label>
                                    Maximum upload file size: <span> {{ videoSizeLimit }}</span>
                                </label>
                            </div>
                        </div>

                        <div class="input-container">
                            <label>Instructional Documents (optional)</label>
                            <app-drag-and-drop-files
                                multiple
                                library-enabled
                                :disabled="isDragAndDropDisabled"
                                :file-accept="docFileConfig.fileSelectorAllowedTypes"
                                :files="solution.documents"
                                label="Choose a file source or drag file here"
                                :loading-files="loadingFiles"
                                :library-file-types="docFileConfig.libraryTypes"
                                @upload="handleDocumentUpload"
                                @library:selected="handleDocumentSelected"
                                @remove="deleteDocumentFile"
                            />
                            <div class="requirements">
                                <label>
                                    Supported file types:
                                    {{ docFileConfig.fileSelectorAllowedTypes.join(', ') }}
                                </label>
                                <label>
                                    Maximum upload file size:
                                    <span>{{ documentSizeLimit }}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div class="bordered-container">
                        <div class="title">Pricing</div>
                        <div class="input-container">
                            <label>Billing Type </label>
                            <div class="billing-type-unit-wrapper">
                                <solution-billing-type-dropdown v-model="solution.billingType" />
                            </div>
                        </div>
                        <div class="input-container">
                            <label>Billing Cycle *</label>
                            <div class="billing-type-unit-wrapper">
                                <solution-billing-cycle-dropdown v-model="solution.billingCycle" />
                            </div>
                        </div>
                        <div class="input-container">
                            <app-input
                                prefix="$"
                                :value="solution.unitPrice"
                                label="Price *"
                                min="0"
                                placeholder="0.00"
                                type="number"
                                :is-valid="!submitted || $v.solution.unitPrice.minValue"
                                error="Unit Price is required"
                                @input="updateValue('unitPrice', +$event)"
                            >
                            </app-input>
                        </div>
                        <div class="input-container">
                            <app-input
                                prefix="$"
                                :value="solution.unitCost"
                                label="Cost *"
                                min="0"
                                type="number"
                                placeholder="0.00"
                                :is-valid="!submitted || $v.solution.unitCost.minValue"
                                error="Unit Cost is required"
                                @input="updateValue('unitCost', +$event)"
                            >
                            </app-input>
                        </div>
                        <div class="input-container">
                            <label>Gross Margin</label>
                            <label :class="['gross-margin', { 'green-gross-margin': greenText }]">
                                ${{ solution.unitMargin }} ({{ unitMarginPercentage }}%)
                            </label>
                        </div>
                    </div>
                    <div class="bordered-container bundles">
                        <div class="title">Bundle</div>
                        <h6>Solutions Bundle</h6>
                        <p>Bundle multiple solutions into an easy-to-manage package deal.</p>
                        <template v-for="(item, index) in solution.bundles">
                            <added-bundled-solution-card
                                v-if="item.action !== 'edit'"
                                :id="`bundle-item-card-${item.id}`"
                                :key="item.id"
                                :solution="item"
                                :type="item.type"
                                @delete-item="removeItem(index)"
                                @edit-inline:bundle-solution="editLineItem(item, index)"
                            />
                            <solution-bundle-item-form
                                v-else
                                :key="item.id + 's'"
                                :bundle-item="item"
                                :position="index"
                                :image-loading="isImageLoading"
                                @save="handleUpdateSolutionToBundle"
                                @cancel="handleCancelEdit(index)"
                                @image:upload="updateImageLoading"
                            />
                        </template>

                        <solution-bundle-item-form
                            v-if="showCreateBundleForm"
                            :bundle-item="customBundle"
                            :image-loading="isImageLoading"
                            @save="handleAddedSolutionToBundle"
                            @cancel="handleCancelCreate"
                            @image:upload="updateImageLoading"
                        />

                        <div v-if="solution.bundles.length > 0" class="bundles-totals-container">
                            <div class="currency-total-container">
                                <p>Bundle Value</p>
                                <span class="amount">{{ bundleValue | currencyTwoDecimals }}</span>
                            </div>
                            <div class="currency-total-container">
                                <p>Solution Price</p>
                                <span class="amount">{{
                                    solution.unitPrice | currencyTwoDecimals
                                }}</span>
                            </div>
                        </div>
                        <div class="bundle-actions-container">
                            <app-button
                                :disabled="showCreateBundleForm || showEditBundleForm"
                                @click="handleSolutionBundleClick('existing')"
                            >
                                Add Existing Solutions <span class="plus-sign">+</span>
                            </app-button>
                            <app-button
                                :disabled="showCreateBundleForm || showEditBundleForm"
                                @click="handleSolutionBundleClick('custom')"
                            >
                                Add Custom Solution <span class="plus-sign">+</span>
                            </app-button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                        Cancel
                    </app-button>
                </div>
                <div class="action-container">
                    <app-button
                        :disabled="isLoading || isImageLoading || isVideoLoading"
                        @click="save"
                    >
                        Save Solution
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AddedBundledSolutionCard = () => import('@/components/solutions/added-bundled-solution-card');
const AppButton = () => import('@/components/common/app-button.vue');
const AppDragAndDropArea = () => import('@/components/common/app-drag-and-drop-area.vue');
const AppDragAndDropFiles = () => import('@/components/common/app-drag-and-drop-files.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const AppTextArea = () => import('@/components/common/app-textarea.vue');
const F6UploadCloudSvg = () => import('@/components/svg/grid/f6-upload-cloud-svg');
const GlobalCategoryDropdown = () => import('@/components/common/global-category-dropdown.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const R21LibraryOutlineSvg = () => import('@/components/svg/grid/r21-library-outline-svg');
const SolutionBillingCycleDropdown = () =>
    import('@/components/solutions/solution-billing-cycle-dropdown.vue');
const SolutionBillingTypeDropdown = () =>
    import('@/components/solutions/solution-billing-type-dropdown.vue');
const SolutionTypeDropdown = () => import('@/components/solutions/solution-type-dropdown.vue');
const SolutionBundleItemForm = () => import('@/components/solutions/solution-bundle-item-form');
const TrashCanSvg = () => import('@/components/svg/trash-can-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

import EventBus from '@/scripts/event-bus.js';
import FileMixin, { FileByteSizeLimitError, FileTypeError } from '@/mixins/file-mixin';
import { FileService } from '@/scripts/services/file/file-service';
import { SolutionService } from '@/scripts/services/solution/solution-service';
import { required } from 'vuelidate/lib/validators';
import { LogLevel } from '@/scripts/logger';
import { isEmpty } from 'lodash';

const minValue = (value) => {
    return value !== null && value >= 0;
};

export default {
    name: 'solution-widget',

    components: {
        'added-bundled-solution-card': AddedBundledSolutionCard,
        'app-button': AppButton,
        'app-drag-and-drop-area': AppDragAndDropArea,
        'app-drag-and-drop-files': AppDragAndDropFiles,
        'app-input': AppInput,
        'app-textarea': AppTextArea,
        'f6-upload-cloud-svg': F6UploadCloudSvg,
        'global-category-dropdown': GlobalCategoryDropdown,
        'loading-screen-svg': LoadingScreenSvg,
        'r21-library-outline-svg': R21LibraryOutlineSvg,
        'solution-billing-cycle-dropdown': SolutionBillingCycleDropdown,
        'solution-billing-type-dropdown': SolutionBillingTypeDropdown,
        'solution-type-dropdown': SolutionTypeDropdown,
        'solution-bundle-item-form': SolutionBundleItemForm,
        'trash-can-svg': TrashCanSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    mixins: [FileMixin],

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            hasError: false,
            videoSrcName: null,
            context: null,
            showCreateBundleForm: false,
            showEditBundleForm: false,
            submitted: false,
            isLoading: false,
            isImageLoading: false,
            isVideoLoading: false,
            loadingFiles: false,
            unitMarginPercentage: 0,
            solution: {
                id: this.widget.solutionId,
                accountId: this.widget.accountId,
                name: null,
                sku: null,
                solutionType: null,
                categories: [],
                description: null,
                productImage: null,
                productImageName: null,
                collateralId: null,
                unitPrice: 0,
                videoUrl: null,
                videoThumbnail: null,
                unitCost: 0,
                unitMargin: 0,
                billingCycle: this.$helpers.getSolutionBillingCycles()[0],
                billingType: null,
                bundles: [],
                documents: [],
            },

            customBundle: {
                name: null,
                price: null,
                description: null,
                productImage: null,
                productImageName: null,
                qty: 1,
                subtotal: null,
                action: 'create',
                mimeType: null,
            },

            imageFileConfig: {
                byteSizeLimit: 26214400, // 25MB
                fileSelectorAllowedTypes: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'],
                allowedFileTypes: [
                    'image/jpg',
                    'image/jpeg',
                    'image/png',
                    'image/gif',
                    'image/webp',
                    'image/bmp',
                ],
            },

            videoFileConfig: {
                byteSizeLimit: 524288000, // 500MB
                fileSelectorAllowedTypes: ['mp4', 'mov', 'avi', 'wmv', 'webm', 'mkv', 'avchd'],
                allowedFileTypes: [
                    'video/mp4',
                    'video/quicktime',
                    'video/x-msvideo',
                    'video/x-ms-wmv',
                    'video/webm',
                    'video/x-matroska',
                    'video/m2ts',
                ],
            },

            docFileConfig: {
                byteSizeLimit: 524288000, // 500MB
                byteSizeMinimum: 0,
                fileSelectorAllowedTypes: [
                    'jpg',
                    'jpeg',
                    'png',
                    'gif',
                    'pdf',
                    'txt',
                    'doc',
                    'docx',
                    'tiff',
                    'ppt',
                    'pptx',
                ],

                allowedFileTypes: [
                    'image/jpg',
                    'image/jpeg',
                    'image/png',
                    'imgage/gif',
                    'application/pdf',
                    'application/txt',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'image/tiff',
                    'application/vnd.ms-powerpoint',
                    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                ],

                libraryTypes: ['Document', 'Image', 'PDF', 'Presentation'],
            },
        };
    },

    validations: {
        solution: {
            name: { required },
            billingCycle: { required },
            unitPrice: { minValue },
            unitCost: { minValue },
        },
    },

    computed: {
        clientId() {
            return this.$store.state.context.client.id;
        },

        agencyId() {
            return this.$store.state.context.agency.id;
        },

        widgetTitle() {
            return `${this.widget.action} Solution`;
        },

        solutionId() {
            return this.widget.solutionId;
        },

        bundleValue() {
            return this.solution.bundles.reduce((prev, curr) => {
                return prev + Number(curr.subtotal);
            }, 0);
        },

        youtubeEmbedUrl() {
            if (this.solution.videoUrl) {
                const videoId = this.solution.videoUrl.match(/v=([^&]+)/);
                return videoId
                    ? `https://www.youtube.com/embed/${videoId[1]}?showinfo=0&controls=0&autohide=1`
                    : null;
            } else {
                return null;
            }
        },

        isDragAndDropDisabled() {
            return this.loadingFiles;
        },

        greenText() {
            return parseFloat(this.unitMarginPercentage) > 50;
        },

        imageSizeLimit() {
            return '25MB';
        },

        videoSizeLimit() {
            return '500MB';
        },

        documentSizeLimit() {
            return '500MB';
        },
    },

    watch: {
        'solution.unitPrice': {
            immediate: true,
            handler() {
                this.solution.unitMargin = this.solution.unitPrice - this.solution.unitCost;
            },
        },

        'solution.unitCost': {
            immediate: true,
            handler() {
                this.solution.unitMargin = this.solution.unitPrice - this.solution.unitCost;
            },
        },

        'solution.unitMargin': {
            immediate: true,
            handler() {
                const percentage = (this.solution.unitMargin / this.solution.unitPrice) * 100 || 0;
                this.unitMarginPercentage = percentage.toFixed(2);
            },
        },
    },

    mounted() {
        EventBus.$on('bundles:add-existing-solutions', this.handleAddedExistingSolutionToBundle);
    },

    destroyed() {
        EventBus.$off('bundles:add-existing-solutions', this.handleAddedExistingSolutionToBundle);
    },

    created() {
        if (this.solutionId) {
            this.getSolution();
        }
    },

    methods: {
        handleLibrarySelect(event) {
            if (this.context === 'video') {
                this.solution.videoUrl = event.url;
            }
        },

        handleDocumentSelected(event) {
            this.solution.documents = this.solution.documents.concat(
                event.map((item) => ({
                    id: item.id,
                    name: item.previewName,
                    size: item.size,
                    type: item.type,
                    url: item.url,
                })),
            );
        },

        handleDelete() {
            this.solution.videoUrl = null;
            this.solution.videoThumbnail = null;
        },

        setContext(ctx) {
            this.context = ctx;
        },

        handleAddedExistingSolutionToBundle(addedSolutions) {
            const filteredAddedSolutions = addedSolutions.filter(
                (solution) => !solution.isCustomBundle,
            );
            const customSolutions = addedSolutions.filter((solution) => solution.isCustomBundle);
            const newAddedSolutions = filteredAddedSolutions.map((solution) => ({
                ...solution,
                bundledSolutionId: solution.bundledSolutionId || solution.id,
                isCustomBundle: false,
            }));
            this.solution.bundles = [...newAddedSolutions, ...customSolutions];
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        updateValue(property, value) {
            this.solution[property] = value;
            this.$v.solution[property].$touch();
        },

        getSolution() {
            this.isLoading = true;
            let expandParams = [
                'Categories($select=Name)',
                'Bundles($select=Id,Name,ImageUrl,Price,Description,Quantity,FileMimeType,BundledSolutionId,IsCustomBundle)',
                'Documents($select=Id,Name,PreviewUrl,Url,FileMimeType,FileSizeInBytes)',
            ];

            const requestConfig = {
                expand: expandParams.join(','),
            };

            SolutionService.get(this.solutionId, requestConfig)
                .then(({ data }) => {
                    this.mapItem(data.value[0]);
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while fetching your solutions.',
                        { error },
                    );
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        mapItem(item) {
            const billingCycles = this.$helpers.getSolutionBillingCycles();
            const billingTypes = this.$helpers.getSolutionBillingTypes();
            const solutionTypes = this.$helpers.getSolutionTypes();

            const billingCycle = billingCycles.find(({ value }) => value === item.BillingCycle);
            const billingType = billingTypes.find(({ value }) => value === item.BillingTypeId);
            const solutionType = solutionTypes.find(({ value }) => value === item.SolutionTypeId);

            this.solution.id = item.Id;
            this.solution.name = item.Name;
            this.solution.sku = item.Sku;
            this.solution.solutionType = solutionType || null;
            this.solution.description = item.Description;
            this.solution.unitPrice = item.UnitPrice;
            this.solution.unitCost = item.UnitCost;
            this.solution.unitMargin = item.UnitMargin;
            this.solution.billingCycle = billingCycle || null;
            this.solution.billingType = billingType || null;
            this.solution.productImage = item.Url;
            this.solution.videoUrl = item.VideoUrl;
            this.solution.videoThumbnail = item.VideoThumbnailUrl;
            this.solution.categories = item.Categories
                ? item.Categories.map((category) => category.Name)
                : null;
            this.solution.bundles = item.Bundles.map((bundle) => ({
                bundledSolutionId: bundle.BundledSolutionId,
                id: bundle.Id,
                name: bundle.Name,
                price: bundle.Price,
                description: bundle.Description,
                productImage: bundle.ImageUrl,
                mimeType: bundle.FileMimeType,
                productImageName: null,
                qty: bundle.Quantity,
                subtotal: null,
                action: 'create',
                isCustomBundle: bundle.IsCustomBundle,
            }));
            this.solution.documents = item.Documents.map((document) => ({
                id: document.Id,
                name: document.Name,
                preview: document.PreviewUrl,
                url: document.Url,
                type: document.FileMimeType,
                size: document.FileSizeInBytes,
            }));
        },

        addCategory(data) {
            this.solution.categories.includes(data.name)
                ? ''
                : this.solution.categories.push(data.name);
        },

        deleteCategory(name) {
            this.solution.categories = this.solution.categories.filter((category) => {
                return name != category;
            });
        },

        save() {
            this.submitted = true;
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            if (this.solutionId) {
                this.patch();
            } else {
                this.post();
            }
        },

        patch() {
            this.isLoading = true;

            const changes = [
                {
                    name: 'Name',
                    value: this.solution.name,
                },
                {
                    name: 'Sku',
                    value: this.solution.sku,
                },
                {
                    name: 'SolutionTypeId',
                    value: this.solution.solutionType ? this.solution.solutionType.value : null,
                },
                {
                    name: 'Categories',
                    value: this.solution.categories ? this.solution.categories : null,
                },
                {
                    name: 'Description',
                    value: this.solution.description,
                },
                {
                    name: 'CollateralId',
                    value: this.solution.collateralId,
                },
                {
                    name: 'ProductImage',
                    value: this.solution.productImage,
                },
                {
                    name: 'ProductImageName',
                    value: this.solution.productImageName,
                },
                {
                    name: 'UnitPrice',
                    value: this.solution.unitPrice,
                },
                {
                    name: 'VideoUrl',
                    value: this.solution.videoUrl,
                },
                {
                    name: 'VideoThumbnailUrl',
                    value: this.solution.videoThumbnail,
                },
                {
                    name: 'UnitCost',
                    value: this.solution.unitCost,
                },
                {
                    name: 'UnitMargin',
                    value: this.solution.unitMargin,
                },
                {
                    name: 'BillingCycle',
                    value: this.solution.billingCycle ? this.solution.billingCycle.value : null,
                },
                {
                    name: 'BillingTypeId',
                    value: this.solution.billingType ? this.solution.billingType.value : null,
                },
                {
                    name: 'Bundles',
                    value: this.mapBundles(this.solution.bundles),
                },
                {
                    name: 'Documents',
                    value: this.mapDocuments(this.solution.documents),
                },
            ];

            SolutionService.patch(this.solutionId, changes)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your solution has been successfully updated.',
                    );
                    EventBus.$emit('solution:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.isLoading = false;
                    this.$helpers.showFailNotification(
                        'An error occurred while updating your solution.',
                        { error },
                    );
                });
        },

        mapBundles(bundles) {
            return bundles.map((item) => ({
                IsCustomBundle: item.isCustomBundle || false,
                BundledSolutionId: item.bundledSolutionId,
                Name: item.name,
                ImageUrl: item.productImage,
                Price: item.price,
                Description: item.description,
                Quantity: item.qty,
                FileMimeType: item.mimeType || null,
            }));
        },

        mapDocuments(documents) {
            return documents.map((item) => ({
                Name: item.name,
                FileSizeInBytes: item.size,
                Url: item.url,
                FileMimeType: item.type || null,
                PreviewUrl: item.preview,
            }));
        },

        post() {
            this.isLoading = true;

            const newSolution = {
                Name: this.solution.name,
                AccountId: this.solution.accountId,
                Sku: this.solution.sku,
                SolutionTypeId: this.solution.solutionType
                    ? this.solution.solutionType.value
                    : null,

                Categories: this.solution.categories,
                Description: this.solution.description,
                CollateralId: this.solution.collateralId,
                ProductImage: this.solution.productImage,
                ProductImageName: this.solution.productImageName,
                BillingCycle: this.solution.billingCycle.value,
                BillingTypeId: this.solution.billingType ? this.solution.billingType.value : null,
                UnitPrice: this.solution.unitPrice,
                UnitCost: this.solution.unitCost,
                UnitMargin: this.solution.unitMargin,
                Bundles: this.mapBundles(this.solution.bundles),
                Documents: this.mapDocuments(this.solution.documents),
                VideoUrl: this.solution.videoUrl,
                VideoThumbnailUrl: this.solution.videoThumbnail,
            };

            SolutionService.post(newSolution)
                .then(() => {
                    this.$helpers.showSuccessNotification(
                        'Your solution has been successfully created.',
                    );
                    EventBus.$emit('solution:update');
                    this.closeWidget();
                })
                .catch((error) => {
                    this.isLoading = false;
                    this.$helpers.showFailNotification(
                        'An error occurred while creating your solution.',
                        { error },
                    );
                });
        },

        handleSolutionBundleClick(e) {
            if (e === 'custom') {
                this.showCreateBundleForm = true;
            }
            if (e === 'existing') {
                const widget = {
                    name: 'add-existing-solution-widget',
                    currentBundleItems: this.solution.bundles,
                    currentSolutionId: this.solution.id,
                };
                EventBus.$emit('open-widget', widget);
            }
        },

        handleCancelCreate() {
            this.showCreateBundleForm = false;
        },

        handleUpload(e) {
            this.getBase64Url(e, this.imageFileConfig)
                .then(({ encodingStr, name }) => {
                    this.solution.productImage = encodingStr;
                    this.solution.productImageName = name;
                })
                .catch((error) => {
                    if (error instanceof FileByteSizeLimitError) {
                        this.$helpers.showFailNotification(
                            `File size exceeds the maximum amount of ${this.imageSizeLimit}s`,
                            { level: LogLevel.INFO },
                        );
                    } else if (error instanceof FileTypeError) {
                        const [head, ...tail] = this.imageFileConfig.fileSelectorAllowedTypes;
                        this.$helpers.showFailNotification(
                            `File needs to be of type ${tail.join(', ')} or ${head}`,
                            { level: LogLevel.INFO },
                        );
                    } else {
                        this.$helpers.showFailNotification(
                            'An error occurred while uploading your image. Please check your format and try again.',
                            { error },
                        );
                    }
                });
        },

        handleVideoUpload(e) {
            return new Promise((resolve, reject) => {
                this.isVideoLoading = true;
                this.getBase64Url(e, this.videoFileConfig)
                    .then(({ encodingStr, name }) => {
                        const videoRequest = this.mediaRequest({ name, url: encodingStr });
                        const videoThumbnailRequest = this.$helpers
                            .createBase64VideoThumbnail(name, encodingStr)
                            .then(({ sourceName, sourceUrl }) => {
                                return this.mediaRequest({
                                    name: sourceName,
                                    url: sourceUrl,
                                });
                            });

                        return Promise.all([videoRequest, videoThumbnailRequest]);
                    })
                    .then((values) => {
                        this.videoSrcName = values[0].fileName;
                        this.solution.videoUrl = values[0].url;
                        this.solution.videoThumbnail = values[1].url;
                    })
                    .then(resolve)
                    .catch((error) => {
                        if (error instanceof FileByteSizeLimitError) {
                            this.$helpers.showFailNotification(
                                `File size exceeds the maximum amount of ${this.videoSizeLimit}s`,
                            );
                        } else if (error instanceof FileTypeError) {
                            const [head, ...tail] = this.videoFileConfig.fileSelectorAllowedTypes;
                            this.$helpers.showFailNotification(
                                `File needs to be of type ${tail.join(', ')} or ${head}`,
                            );
                        } else {
                            this.$helpers.showFailNotification(
                                'An error occurred while uploading your video. Please check your format and try again.',
                            );
                        }
                        reject();
                    })
                    .finally(() => {
                        this.isVideoLoading = false;
                    });
            });
        },

        getFilesFromSource(sourceEvent) {
            if (sourceEvent.currentTarget.files) {
                return sourceEvent.currentTarget.files;
            } else if (sourceEvent.dataTransfer) {
                return sourceEvent.dataTransfer.files;
            } else if (sourceEvent) {
                if (sourceEvent.dataTransfer) {
                    return sourceEvent.dataTransfer.files;
                } else if (sourceEvent.target.files) {
                    return sourceEvent.target.files;
                }
            }
            return null;
        },

        handleDocumentUpload(e) {
            let files = this.getFilesFromSource(e);
            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                try {
                    if (file.size < this.docFileConfig.byteSizeMinimum) {
                        throw new Error('Low Size');
                    }
                    if (file.size > this.docFileConfig.byteSizeLimit) {
                        throw new Error('Over Size');
                    }

                    let fileInfo = {
                        name: file.name,
                        size: file.size,
                        type: file.type,
                    };
                    this.loadingFiles = true;
                    this.$helpers
                        .getBase64Encoding(file)
                        .then((encodingStr) => {
                            return this.mediaRequest(
                                { name: fileInfo.name, url: encodingStr },
                                { filePreview: 1 },
                            );
                        })
                        .then(({ url, fileName, preview }) => {
                            this.solution.documents.push({
                                id: this.$helpers.createId('doc'),
                                name: fileName,
                                size: fileInfo.size,
                                type: fileInfo.type,
                                url: url,
                                preview: preview,
                            });
                        })
                        .catch((error) => {
                            this.$helpers.showFailNotification(
                                'An error occurred while uploading your document. Please try again.',
                                { error },
                            );
                        })
                        .finally(() => {
                            this.loadingFiles = false;
                        });
                } catch (error) {
                    if (error.message === 'Over Size') {
                        this.$helpers.showFailNotification('File size exceeds the maximum amount', {
                            level: LogLevel.INFO,
                        });
                    } else if (error.message === 'Low Size') {
                        this.$helpers.showFailNotification('Low file size', {
                            level: LogLevel.INFO,
                        });
                    } else {
                        this.$helpers.showFailNotification(
                            'An error occurred while uploading your document. Please check your format and try again.',
                            { error },
                        );
                    }
                }
            }
        },

        deleteDocumentFile({ id }) {
            this.solution.documents = this.solution.documents.filter((file) => file.id !== id);
        },

        mediaRequest(element, queries = {}) {
            return new Promise((resolve, reject) => {
                const media = {
                    name: element.name,
                    url: element.url,
                };
                this.clientId && this.$set(media, 'clientId', this.clientId);
                this.agencyId && this.$set(media, 'agencyId', this.agencyId);
                FileService.post(media, queries)
                    .then(({ data }) => {
                        const uploadedMedia = {
                            fileName: element.name,
                        };

                        if (isEmpty(queries)) {
                            this.$set(uploadedMedia, 'url', data);
                        } else {
                            this.$set(uploadedMedia, 'url', data.Url);
                            this.$set(uploadedMedia, 'preview', data.Preview);
                        }

                        resolve(uploadedMedia);
                    })
                    .catch(() => {
                        this.$helpers.showFailNotification(`Error trying to save ${element.name}`);
                        reject();
                    });
            });
        },

        updateImageLoading(value) {
            this.isImageLoading = value;
        },

        deleteFile() {
            this.solution.collateralId = null;
            this.solution.productImage = null;
            this.solution.productImageName = null;
        },

        handleCollateralSelected({ id, previewImageUrl }) {
            if (this.context === 'product') {
                this.solution.collateralId = id;
                this.solution.productImage = previewImageUrl;
            }
        },

        handleIconSelected({ name, url }) {
            if (this.context === 'product') {
                this.solution.productImage = url;
                this.solution.productImageName = name;
            }
        },

        removeItem(position) {
            this.solution.bundles.splice(position, 1);
        },

        editLineItem(item, index) {
            this.showEditBundleForm = true;
            this.solution.bundles[index].action = 'edit';
        },

        handleCancelEdit(index) {
            this.solution.bundles[index].action = 'create';
            this.showEditBundleForm = false;
        },

        handleAddedSolutionToBundle(item) {
            const newItem = {
                ...item,
                isCustomBundle: true,
            };
            this.solution.bundles.push(newItem);
        },

        handleUpdateSolutionToBundle(item, index) {
            const filteredItem = {
                ...item,
                bundledSolutionId: null,
                isCustomBundle: true,
            };
            this.solution.bundles = this.solution.bundles.toSpliced(index, 1, filteredItem);
        },

        isValidYoutubeUrl(url) {
            const youtubeUrlPattern =
                /^(https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/(watch\?v=[\w-]+.*|channel\/[\w-]+.*|playlist\?list=[\w-]+.*))$/;
            return youtubeUrlPattern.test(url);
        },

        handleInput(value) {
            if (value === '') {
                this.handleDelete();
            } else {
                if (this.isValidYoutubeUrl(value)) {
                    const params = new URLSearchParams(new URL(value).search);
                    const videoId = params.get('v');
                    this.solution.videoThumbnail = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
                    this.solution.videoUrl = value;
                    this.hasError = false;
                } else {
                    this.hasError = true;
                }
            }
        },
    },
};
</script>

<style>
.solution-widget {
    position: relative;
}

.solution-widget .widget-contents-container .content-wrapper {
    padding: 16px 24px;
}

.solution-widget .content-wrapper .form-wrapper {
    display: flex;
    flex-direction: column;
    gap: 32px;
}

.solution-widget .bordered-container:first-child {
    margin-top: 12px;
}

.solution-widget .bordered-container {
    border: 1px solid #cac6cb;
    border-radius: 22px;
    margin-bottom: 2.5em;
    padding: 24px;
    position: relative;
}

.solution-widget .bordered-container .title {
    background-color: #ffffff;
    background-clip: content-box;
    font: 500 18px 'Roboto';
    padding: 0 30%;
    text-align: center;
    position: absolute;
    top: -11px;
    left: 0;
    width: 100%;
}

.solution-widget .widget-contents-container .transparent-btn {
    background: transparent;
    color: #877d88;
    border: none;
}

.solution-widget .widget-contents-container .transparent-btn:hover {
    background: #e1e0e5;
}

.solution-widget .app-input.has-icon .input-container .icon-container {
    right: initial;
    left: 10px;
    top: 8px;
}

.solution-widget .app-input.has-icon input {
    padding: 7px 15px 7px 35px;
}

.solution-widget .app-input .icon-container path {
    fill: #2ada2b;
}

.solution-widget .input-container.split {
    display: flex;
    justify-content: space-between;
}

.solution-widget .input-container.split .app-input {
    width: 48%;
}

.solution-widget .input-container .loading-screen {
    margin: auto;
    text-align: left;
}

.solution-widget .input-container .app-drag-and-drop-area {
    display: inline-block;
    width: 100%;
}

.solution-widget .input-container .app-drag-and-drop-area .multiple-image-preview {
    display: none;
}

.solution-widget .bordered-container hr {
    border-top: 2px solid #e9e9ef;
    margin: 0;
}

.solution-widget .bordered-container .bundle-actions-container {
    margin-top: 16px;
    display: flex;
    gap: 16px;
}

.solution-widget .bordered-container .bundles-totals-container {
    align-items: flex-end;
    border-bottom: 1px solid #cac6cb;
    display: flex;
    flex-direction: column;
    padding: 16px 0;
    row-gap: 16px;
}

.solution-widget .bordered-container .bundles-totals-container .currency-total-container {
    align-items: center;
    display: flex;
    gap: 24px;
    width: 210px;
    justify-content: space-between;
}

.solution-widget .bordered-container .bundles-totals-container .currency-total-container > p {
    text-align: left;
    font: normal normal bold 16px/19px Roboto;
    letter-spacing: 0px;
    color: #382153;
    margin: 0;
}

.solution-widget .bordered-container .bundles-totals-container .currency-total-container > span {
    text-align: left;
    font: normal normal normal 16px/19px Roboto;
    letter-spacing: 0px;
    color: #382153;
}

.solution-widget .bordered-container > .input-container:not(:last-child) {
    margin-bottom: 16px;
}

.solution-widget .bordered-container .billing-info .input-container {
    margin-top: 0;
}

.solution-widget .input-container > label {
    display: block;
    font: 500 16px/1em 'Roboto';
    margin-bottom: 10px;
    color: #382153;
}

.solution-widget .plus-sign {
    font-size: 22px;
    margin-left: 8px;
}

.solution-widget .preview-image-solutions {
    display: inline-block;
    margin-top: 16px;
    position: relative;
    width: 152px;
    height: 144px;
}

.solution-widget .preview-image-solutions img {
    border: 1px solid #cac6cb;
    border-radius: 16px;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.solution-widget .preview-video-solutions {
    display: inline-block;
    margin-top: 16px;
    position: relative;
}

.solution-widget .preview-video-solutions .ratio-box {
    width: 256px;
    height: 144px;
    position: relative;
    padding-bottom: 56.25%;
}

.solution-widget .preview-video-solutions iframe {
    border-radius: 12px;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}

.solution-widget .preview-video-solutions video {
    border: 1px solid #cac6cb;
    width: 256px;
    height: 144px;
    border-radius: 12px;
}

.solution-widget .trash-can {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: #ee206f 0% 0% no-repeat padding-box;
    position: absolute;
    display: flex;
    top: -12px;
    right: -12px;
    cursor: pointer;
    z-index: 40;
}

.solution-widget .trash-can .trash-can-svg {
    width: 14px;
    height: 18px;
    margin: auto;
}

.solution-widget .trash-can .trash-can-svg path,
.solution-widget .trash-can .trash-can-svg rect {
    fill: #fff;
}

.solution-widget .app-drag-and-drop-area-uploader .app-input.video-input {
    width: 280px;
}

/*Hide textarea scrollbar to not disrupt the overall widget style */
.solution-widget .input-container .app-textarea .textarea-container textarea::-webkit-scrollbar {
    width: 12px;
    background: transparent;
}

.solution-widget
    .input-container
    .app-textarea
    .textarea-container
    textarea::-webkit-scrollbar-thumb {
    background-color: transparent;
    border-radius: 16px;
    background-clip: content-box;
}

.solution-widget .input-container .mdf-drag-and-drop-files .files-preview-container .files-title {
    display: none;
}

.solution-widget .input-container .requirements {
    display: flex;
    flex-direction: column;
}

.solution-widget .input-container .requirements label {
    font: 400 16px/24px 'Roboto';
    color: #877d88;
    margin: 0;
    margin-top: 4px;
}

.solution-widget .input-container .requirements span {
    font-weight: 500;
}
.solution-widget .input-container .gross-margin {
    margin-top: 24px;
    margin-left: 16px;
}
.solution-widget .input-container .green-gross-margin {
    color: #2ada2b;
}

.solution-widget
    .app-drag-and-drop-area
    .app-drag-and-drop-area-uploader
    .button-container
    svg
    path {
    fill: transparent;
    stroke: #fff;
}

.solution-widget .input-container .app-drag-and-drop-files .app-drag-and-drop-files-uploader {
    padding: 20px;
}
.solution-widget .input-container .app-drag-and-drop-files .app-drag-and-drop-files-uploader label {
    font-size: 16px;
    margin-top: 10px;
    font-weight: 600;
}
</style>
